// src/oura/merchant/HomeMonth.tsx
//
// "This month with your AI team" on Home under the redesign (19 Oct 2026):
// how far this month's work has got, one segment per piece, then what is
// coming up, what is in progress and what is done, side by side. Every date
// is on Your plan (PlanDrawer), which each date opens (21 Oct 2026); every
// piece of work is on Our work, which the head and each line open (27 Oct 2026).
//
// The count is over measured work only (staging 3fdc24e6), the rule Work and
// the switched-off tile use, so "5 of 12 done" means the same everywhere.

import { CheckIcon, ClipboardListIcon } from "lucide-react";
import type { ReactNode } from "react";
import type { ClientPagePayload, ClientPlanItem, ClientPlanStatus } from "@service/aeo/clientReview";
import { ArrowLink, CardEmpty, FOCUS_RING, SURFACE, TYPE, ROW_HOVER_TIGHT } from "@oura/ui";
import { cn } from "../lib/cn";
import { OURA_ROOT } from "../nav";
import { calendarDay } from "./workDates";
import { DateChip, ItemIcon } from "./WorkMarks";
import { dayDate } from "../lib/oura";
import { measuredItems, planLineTitle, sortPlanItems, type ComingDate } from "./model";

/** Lines each panel names before "and N more". */
const PANEL_SHOWN = 4;

const MONTH_NO: Record<string, string> = {
    jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
    jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
};

/**
 * The last day of this month of the term, from the window the engine wrote
 * ("18 Sep to 17 Oct 2026"), as YYYY-MM-DD. Null when it does not read: the
 * card then shows the dates as it always did, rather than guess a month.
 */
export function monthWindowEnd(window: string | null | undefined): string | null {
    const m = window?.match(/to\s+(\d{1,2})\s+([A-Za-z]{3})[A-Za-z]*\s+(\d{4})\s*$/);
    const month = m ? MONTH_NO[m[2].toLowerCase()] : undefined;
    return m && month ? `${m[3]}-${month}-${m[1].padStart(2, "0")}` : null;
}

/**
 * "Coming up" on a THIS MONTH card names this month's dates only. It listed the
 * whole term's, so a November date sat in October's card (Brickhouse, 21 Oct
 * 2026). The rest are one line away on Your plan, which the "later" note opens.
 * Nothing left this month: the next date alone, so the panel is never blank
 * while the term goes on.
 */
export function monthDates(dates: ComingDate[], end: string | null): { shown: ComingDate[]; later: number } {
    if (!end) return { shown: dates, later: 0 };
    const inMonth = dates.filter((d) => d.date.slice(0, 10) <= end);
    const shown = inMonth.length > 0 ? inMonth : dates.slice(0, 1);
    return { shown, later: dates.length - shown.length };
}

/**
 * Each piece's segment, in one blue family so no colour needs a key of its
 * own: solid done, light moving, an outline where it needs them, grey not
 * started. The same swatch sits before each count in the line above, so the
 * line is the legend (Jack: the light blue and amber read as nothing).
 */
const SEGMENT: Record<ClientPlanStatus, string> = {
    done: "bg-blue-600",
    in_progress: "bg-blue-200",
    with_google: "bg-blue-200",
    waiting_on_you: "bg-white ring-[1.5px] ring-inset ring-blue-600",
    not_started: "bg-gray-200",
    reported_by_hand: "bg-gray-200",
};

/** A count with its segment's swatch before it. */
function Counted({ swatch, children }: { swatch: string; children: ReactNode }) {
    return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span aria-hidden className={cn("h-2 w-3.5 shrink-0 rounded-full", swatch)} />
            {children}
        </span>
    );
}


export function MonthCard({
    payload,
    dates,
    onOpenDates,
    onOpenLine,
}: {
    payload: ClientPagePayload;
    dates: ComingDate[];
    /** Opens Your plan; a date opens it too, since the dates live there. */
    onOpenDates: () => void;
    /** Opens one line, by key or code, where the list is (Our work). */
    onOpenLine: (key: string) => void;
}) {
    const { plan, subscription, schedule } = payload;
    // No plan and no subscription: nothing has been sold yet (#3710).
    const items = plan && subscription ? sortPlanItems(plan.items) : [];
    const inProgress = items.filter((i) => i.status === "in_progress" || i.status === "with_google");
    const finished = items.filter((i) => i.status === "done");
    const thisMonth = schedule?.months.find((m) => m.termMonth === schedule.currentMonth);
    const coming = monthDates(dates, monthWindowEnd(thisMonth?.window));

    return (
        <section aria-labelledby="home-month" className={cn(SURFACE.card, "flex flex-col gap-5 p-6")}>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <h2 id="home-month" className={cn(TYPE.tt, "m-0 text-gray-900")}>
                    This month with your AI team
                </h2>
                {/* The list itself is on Our work, once (27 Oct 2026). */}
                {items.length > 0 && <ArrowLink to={`${OURA_ROOT}/work`}>See all our work</ArrowLink>}
            </div>
            {items.length === 0 ? (
                <CardEmpty icon={ClipboardListIcon} title="No work scheduled yet">
                    Your account manager is preparing your plan. This fills in the moment it is activated.
                </CardEmpty>
            ) : (
                <>
                    <WorkProgress items={items} schedule={schedule} />
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <Panel
                            title="Coming up"
                            empty="Your dates appear here once they are published."
                            more={coming.shown.length - PANEL_SHOWN}
                            after={coming.later > 0 ? (
                                <button type="button" onClick={onOpenDates} className={cn(TYPE.tc, "m-0 self-start rounded text-left font-medium text-blue-700 hover:underline", FOCUS_RING)}>
                                    {coming.later === 1 ? "1 more date after this month" : `${coming.later} more dates after this month`}
                                </button>
                            ) : undefined}
                        >
                            {coming.shown.slice(0, PANEL_SHOWN).map((d, i) => (
                                <li key={d.key}>
                                    <button type="button" onClick={onOpenDates} className={cn("flex w-full items-center gap-3 rounded text-left", ROW_HOVER_TIGHT, FOCUS_RING)}>
                                        {/* Our work's calendar tile, smaller (27 Oct 2026). */}
                                        <DateChip date={calendarDay(d.date)} next={i === 0} small />
                                        <span className={cn(TYPE.tb, "min-w-0 text-gray-900")}>
                                            <span className="sr-only">{dayDate(d.date) ?? "Date to be set"}: </span>
                                            {d.label}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </Panel>
                        <Panel title="In progress" empty="Nothing in progress right now." more={inProgress.length - PANEL_SHOWN}>
                            {inProgress.slice(0, PANEL_SHOWN).map((i) => (
                                <LineRow key={i.key ?? i.code} item={i} status="in_progress" onOpen={onOpenLine} />
                            ))}
                        </Panel>
                        <Panel title="Done" empty="The first pieces of work land here as they finish." more={finished.length - PANEL_SHOWN}>
                            {finished.slice(0, PANEL_SHOWN).map((i) => (
                                <LineRow key={i.key ?? i.code} item={i} status="done" onOpen={onOpenLine} />
                            ))}
                        </Panel>
                    </div>
                </>
            )}
        </section>
    );
}


/**
 * How far the work has got: the counts, each with its swatch, and one segment
 * per piece. Home's month card and Our work draw the same line (27 Oct 2026).
 * Over measured work only, so "5 of 12 done" means the same everywhere.
 */
export function WorkProgress({ items, schedule }: { items: ClientPlanItem[]; schedule: ClientPagePayload["schedule"] }) {
    const measured = measuredItems(items);
    const count = (...s: ClientPlanStatus[]) => measured.filter((i) => s.includes(i.status)).length;
    const done = count("done");
    const moving = count("in_progress", "with_google");
    const theirs = count("waiting_on_you");
    const notStarted = count("not_started");
    if (measured.length === 0) return null;
    return (
        <div className="flex flex-col gap-2.5">
            <p className={cn(TYPE.tb, "m-0 flex flex-wrap justify-between gap-x-4 gap-y-1")}>
                <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <Counted swatch={SEGMENT.done}>
                        <span className="font-semibold text-gray-900">
                            {done} of {measured.length} done
                        </span>
                    </Counted>
                    {moving > 0 && <Counted swatch={SEGMENT.in_progress}><span className="text-gray-600">{moving} in progress</span></Counted>}
                    {theirs > 0 && <Counted swatch={SEGMENT.waiting_on_you}><span className="text-gray-600">{theirs} {theirs === 1 ? "needs" : "need"} you</span></Counted>}
                    {notStarted > 0 && <Counted swatch={SEGMENT.not_started}><span className="text-gray-600">{notStarted} not started</span></Counted>}
                </span>
                {schedule && (
                    <span className={TYPE.lab}>
                        Month {schedule.currentMonth} of {schedule.months.length}
                    </span>
                )}
            </p>
            <div aria-hidden className="grid gap-1" style={{ gridTemplateColumns: `repeat(${measured.length}, minmax(0, 1fr))` }}>
                {/* Done first, then moving, theirs and not started, so the bar fills from the left. */}
                {[...measured]
                    .sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status))
                    .map((i) => (
                        <span key={i.key ?? i.code} className={cn("h-1.5 rounded-full", SEGMENT[i.status])} />
                    ))}
            </div>
        </div>
    );
}

/** A line in a panel: it opens that line on Our work. */
function LineRow({ item, status, onOpen }: { item: ClientPlanItem; status: "in_progress" | "done"; onOpen: (key: string) => void }) {
    return (
        <li>
            <button type="button" onClick={() => onOpen(item.key ?? item.code)} className={cn("flex w-full items-center gap-2.5 rounded text-left", ROW_HOVER_TIGHT, FOCUS_RING)}>
                {/* What the work is, tinted for where it stands: Our work's icon (27 Oct 2026). */}
                <ItemIcon code={item.code} status={status} />
                <span className={cn(TYPE.tb, "min-w-0 text-gray-900")}>{planLineTitle(item)}</span>
            </button>
        </li>
    );
}

/** A line's mark: a grey ring not started, a blue ring in progress, an amber dot needs you, a green tick done. */
export function PanelMark({ status }: { status: ClientPlanStatus }) {
    if (status === "done") return <CheckIcon aria-hidden size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-emerald-600" />;
    // Needs you is the one amber: theirs to act on, never mistaken for our blue (27 Oct 2026).
    if (status === "waiting_on_you") return <span aria-hidden className="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full bg-amber-500" />;
    const moving = status === "in_progress" || status === "with_google";
    return <span aria-hidden className={cn("mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full border-2", moving ? "border-blue-600" : "border-gray-300")} />;
}

const ORDER: ClientPlanStatus[] = ["done", "in_progress", "with_google", "waiting_on_you", "not_started", "reported_by_hand"];

/**
 * A grey panel inside the card: a label, up to four lines, and how many more
 * Our work has. To do's With us board draws its columns with it too, so the
 * two boards are one design.
 */
export function Panel({ title, empty, more = 0, after, children }: { title: string; empty: string; more?: number; after?: ReactNode; children: ReactNode[] }) {
    return (
        <div className="flex flex-col gap-3 rounded-xl bg-gray-50 p-4">
            <p className={cn(TYPE.lab, "m-0")}>{title}</p>
            {children.length === 0 ? (
                <p className={cn(TYPE.tc, "m-0 text-gray-500")}>{empty}</p>
            ) : (
                <ul className="list-plain m-0 flex flex-col gap-3 p-0">{children}</ul>
            )}
            {more > 0 && <p className={cn(TYPE.tc, "m-0 text-gray-500")}>and {more} more</p>}
            {after}
        </div>
    );
}
